import { useEffect, useState } from "react";
import { api, type AdminOverview, type AdminUserRow, type PlatformInfo } from "../api";
import { diagnose } from "../lib/diagnose";
import { MiniChart, TrendCard } from "./MiniChart";
import { PlatformIcon } from "./PlatformIcon";

/** Short, safe date for the Users table — "Sep 8, 2026", or "—" when absent. */
const fmtDate = (s: string | null): string => {
  if (!s) return "—";
  const d = new Date(s);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

/**
 * Command Center — the owner-only "god view" over every user of the app. The Ops
 * entry point renders only for the owner (App reads `data.isOwner`) and the
 * endpoint itself 403s non-owners, so this simply surfaces the owner-gated
 * snapshot: KPI tiles, 30-day growth charts, system health, a live activity feed
 * across all users, the top users, the platform breakdown, and recent failures.
 * Every aggregate is independently nullable — a null field degrades to "—" or an
 * empty state rather than blanking the cockpit.
 */

// Failure-rate thresholds (fractions) that pick a tile's semantic colour.
const FAILURE_WARN = 0.05;
const FAILURE_ERR = 0.15;

// X (Twitter) is the one platform that charges per post (~$0.20 with a URL);
// every other platform's publish API is free. So a user's estimated platform
// cost is driven almost entirely by their X volume. Kept as a labelled estimate.
const X_COST_PER_POST = 0.2;

type Tone = "ok" | "warn" | "err" | "neutral";

function failureTone(rate: number): Tone {
  if (rate >= FAILURE_ERR) return "err";
  if (rate >= FAILURE_WARN) return "warn";
  return "ok";
}

/** Render a number, or an em-dash when the aggregate came back null. */
function num(value: number | null | undefined): string {
  return value == null ? "—" : value.toLocaleString();
}

/** Compact big numbers for chart totals: 1234 -> "1.2k". */
function compact(value: number): string {
  if (value < 1000) return String(value);
  if (value < 1_000_000) return `${(value / 1000).toFixed(value < 10_000 ? 1 : 0)}k`;
  return `${(value / 1_000_000).toFixed(1)}M`;
}

/** "just now" / "5m" / "3h" / "2d" — compact relative time for the feed. */
function timeAgo(ms: number): string {
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 45) return "just now";
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86_400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86_400)}d`;
}


function StatTile({
  label,
  value,
  sub,
  tone = "neutral",
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: Tone;
}) {
  return (
    <div className={`ops-tile ops-tone-${tone}`}>
      <div className="ops-tile-label">{label}</div>
      <div className="ops-tile-value">{value}</div>
      {sub && <div className="ops-tile-sub muted small">{sub}</div>}
    </div>
  );
}

/** Look up a platform's display info by id. */
function platformInfo(platforms: PlatformInfo[], id: string): PlatformInfo | undefined {
  return platforms.find((p) => p.id === id);
}

/** One failed post as the overview returns it — the shape the detail modal shows. */
type FailureItem = NonNullable<AdminOverview["recentFailures"]>[number];
/** One row of the top-users table — the shape the per-user drill-down shows. */
type TopUserItem = NonNullable<AdminOverview["topUsers"]>[number];

export function OpsDashboard({ platforms }: { platforms: PlatformInfo[] }) {
  const [data, setData] = useState<AdminOverview | null>(null);
  const [users, setUsers] = useState<AdminUserRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [dlError, setDlError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  // The failure the operator clicked into, and whether its summary was copied.
  const [detail, setDetail] = useState<FailureItem | null>(null);
  const [copied, setCopied] = useState(false);
  // The user the operator clicked into (top-users drill-down).
  const [userDetail, setUserDetail] = useState<TopUserItem | null>(null);

  const load = () => {
    setError(null);
    api
      .adminOverview()
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Couldn't load ops data."));
    // The users list is independent — a failure here shouldn't blank the cockpit.
    api
      .adminUsers()
      .then((r) => setUsers(r.users))
      .catch(() => setUsers(null));
  };

  useEffect(load, []);

  // Push consented users + waitlist leads into the Resend marketing Audience.
  async function syncAudience() {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const r = await api.syncMarketingAudience();
      setSyncMsg(
        `Synced ${r.synced} contact${r.synced === 1 ? "" : "s"} to Resend` +
          (r.failed > 0 ? ` (${r.failed} failed)` : "") + ".",
      );
    } catch (e) {
      setSyncMsg(
        e instanceof Error ? e.message : "Couldn't sync the audience — is RESEND_AUDIENCE_ID set?",
      );
    } finally {
      setSyncing(false);
    }
  }

  // Shared blob → file-download helper for the owner CSV exports.
  async function download(kind: "waitlist" | "users") {
    setDownloading(true);
    setDlError(null);
    try {
      const blob = kind === "waitlist" ? await api.waitlistCsv() : await api.usersCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "waitlist" ? "broadside-waitlist.csv" : "broadside-users.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setDlError(e instanceof Error ? e.message : `Couldn't download the ${kind} export.`);
    } finally {
      setDownloading(false);
    }
  }

  // Build a paste-ready summary of a failure (raw error + diagnosis + suggested
  // fix per leg) and copy it, so the operator can hand it straight to Claude.
  async function copyForClaude(f: FailureItem) {
    const text = [
      `Broadside publish failure — "${f.title || "Untitled post"}" (${new Date(f.at).toLocaleString()})`,
      ...f.failures.map((leg) => {
        const d = diagnose(leg.reason);
        const name = platformInfo(platforms, leg.platform)?.name ?? leg.platform;
        return `- ${name}: ${leg.reason}\n  Diagnosis: ${d.diagnosis}\n  Suggested fix: ${d.action}`;
      }),
      "",
      "What should I do about this?",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  }

  if (error) {
    return (
      <div className="card">
        <p className="error">{error}</p>
        <button type="button" className="btn small" onClick={load}>
          Retry
        </button>
      </div>
    );
  }

  if (!data) {
    return <p className="muted small">Loading…</p>;
  }

  const posts = data.posts;
  const revenue = data.revenue;
  const accounts = data.connectedAccounts;
  const health = data.health;
  const failureRate = posts ? posts.failureRate : null;
  const activeSubs = revenue ? revenue.activeByPlan.starter + revenue.activeByPlan.pro : null;

  const breakdown = accounts
    ? Object.entries(accounts.byPlatform).sort((a, b) => b[1] - a[1])
    : [];

  // Health tone: overdue scheduled or accounts needing reconnect are the alarms.
  const healthTone: Tone = !health
    ? "neutral"
    : health.overdueScheduled > 0 || health.reconnectNeeded > 0
      ? "warn"
      : "ok";

  return (
    <div className="ops god">
      <div className="ops-hud-head">
        <span className="ops-hud-title">Command Center</span>
        <span className="ops-live" aria-label="Live data">
          <span className="ops-live-dot" aria-hidden="true" />
          LIVE
        </span>
        <button type="button" className="btn small ghost ops-refresh" onClick={load}>
          Refresh
        </button>
      </div>

      <div className="ops-grid">
        <StatTile label="Users" value={num(data.users)} sub="workspace owners" />
        <StatTile
          label="MRR"
          value={revenue ? `$${revenue.mrr.toLocaleString()}` : "—"}
          sub="monthly recurring"
          tone={revenue && revenue.mrr > 0 ? "ok" : "neutral"}
        />
        <StatTile
          label="Posts (30d)"
          value={num(posts?.last30d)}
          sub={posts ? `${num(posts.total)} all-time` : undefined}
        />
        <StatTile
          label="Failure rate"
          value={failureRate == null ? "—" : `${(failureRate * 100).toFixed(1)}%`}
          sub={posts ? `${num(posts.failed)} of ${num(posts.total)} failed` : undefined}
          tone={failureRate == null ? "neutral" : failureTone(failureRate)}
        />
        <StatTile
          label="Active subs"
          value={num(activeSubs)}
          sub={
            revenue
              ? `${revenue.activeByPlan.starter} starter · ${revenue.activeByPlan.pro} pro`
              : undefined
          }
        />
        <StatTile
          label="Credits outstanding"
          value={num(revenue?.creditsOutstanding)}
          sub="unspent, across all users"
        />
        <StatTile label="Pending scheduled" value={num(data.scheduledPending)} sub="queued posts" />
        <StatTile
          label="Connected accounts"
          value={num(accounts?.total)}
          sub={accounts ? `${breakdown.length} platforms` : undefined}
        />
        <StatTile
          label="Waitlist"
          value={num(data.waitlist?.total)}
          sub="early-access signups"
          tone={data.waitlist && data.waitlist.total > 0 ? "ok" : "neutral"}
        />
      </div>

      <section className="card ops-section ops-users">
        <div className="ops-section-head">
          <h2>Users{users ? ` · ${users.length}` : ""}</h2>
          <button
            type="button"
            className="btn small ghost"
            disabled={downloading}
            onClick={() => download("users")}
          >
            {downloading ? "…" : "Download CSV"}
          </button>
        </div>
        {users == null ? (
          <p className="muted small">Loading…</p>
        ) : users.length === 0 ? (
          <p className="muted small">No users yet.</p>
        ) : (
          <div className="ops-table-wrap">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Joined</th>
                  <th>Last active</th>
                  <th>Plan</th>
                  <th className="ops-num">Posts</th>
                  <th className="ops-num">Credits</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.email}>
                    <td className="ops-user-email">{u.email}</td>
                    <td>{fmtDate(u.joined)}</td>
                    <td>{fmtDate(u.last_active)}</td>
                    <td>
                      {u.paid ? (
                        <span className="ops-badge ok">{u.tier || "paid"}</span>
                      ) : (
                        <span className="ops-badge">free</span>
                      )}
                    </td>
                    <td className="ops-num">{num(u.posts)}</td>
                    <td className="ops-num">{num(u.credits)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card ops-section">
        <div className="ops-section-head">
          <h2>Growth · last 30 days</h2>
        </div>
        {data.growth == null ? (
          <p className="muted small">Unavailable.</p>
        ) : (
          <div className="ops-charts">
            <TrendCard label="Signups" points={data.growth.map((g) => g.signups)} format={compact} />
            <TrendCard label="Posts" points={data.growth.map((g) => g.posts)} format={compact} />
            <TrendCard label="Waitlist" points={data.growth.map((g) => g.waitlist)} format={compact} />
          </div>
        )}
      </section>

      {revenue && (
        <section className="card ops-section">
          <div className="ops-section-head">
            <h2>Subscriptions &amp; revenue</h2>
            <div className="ops-head-actions">
              <button
                type="button"
                className="btn small"
                onClick={() => void download("users")}
                disabled={downloading}
                title="Every user with tier, paid status, and marketing consent — for segmentation"
              >
                {downloading ? "Preparing…" : "Download users (CSV)"}
              </button>
              <button
                type="button"
                className="btn small"
                onClick={() => void syncAudience()}
                disabled={syncing}
                title="Push consented users + waitlist leads into the Resend marketing Audience"
              >
                {syncing ? "Syncing…" : "Sync to Resend"}
              </button>
            </div>
          </div>
          <p className="muted small ops-users-hint">
            Export includes free / signed-up-but-never-paid accounts — filter by <code>tier</code>{" "}
            and <code>marketing_opt_in</code>. “Sync to Resend” adds consented users + waitlist to
            your marketing Audience (campaigns + unsubscribe are handled in Resend).
          </p>
          {syncMsg && <p className="muted small">{syncMsg}</p>}
          <div className="ops-rev-top">
            <div className="ops-rev-big">
              <span className="ops-rev-n">${revenue.mrr.toLocaleString()}</span>
              <span className="ops-rev-l">MRR</span>
            </div>
            <div className="ops-rev-big">
              <span className="ops-rev-n">${(revenue.mrr * 12).toLocaleString()}</span>
              <span className="ops-rev-l">ARR</span>
            </div>
            <div className="ops-rev-big">
              <span className="ops-rev-n">{revenue.paying.toLocaleString()}</span>
              <span className="ops-rev-l">paying subs</span>
            </div>
            <div className="ops-rev-big">
              <span className="ops-rev-n">
                ${revenue.paying > 0 ? Math.round(revenue.mrr / revenue.paying) : 0}
              </span>
              <span className="ops-rev-l">ARPU / mo</span>
            </div>
          </div>

          {data.mrrHistory && data.mrrHistory.length >= 2 ? (
            <div className="ops-chartcard ops-rev-chart">
              <div className="ops-chartcard-head">
                <span className="ops-chartcard-label">MRR over time</span>
                <span className="ops-chartcard-total">${revenue.mrr.toLocaleString()}</span>
              </div>
              <MiniChart points={data.mrrHistory.map((m) => m.mrr)} />
              <div className="ops-chartcard-foot">
                <span className="muted small">{data.mrrHistory.length}-day history</span>
              </div>
            </div>
          ) : (
            <p className="muted small ops-rev-building">
              📈 MRR-over-time is now being recorded daily — the trend line appears here once
              there are a couple of days of history.
            </p>
          )}

          <table className="ops-rev-table">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Price</th>
                <th>Subs</th>
                <th>MRR</th>
              </tr>
            </thead>
            <tbody>
              {(
                [
                  ["Starter", revenue.prices.starter, revenue.activeByPlan.starter],
                  ["Pro", revenue.prices.pro, revenue.activeByPlan.pro],
                  ["Agency", revenue.prices.agency, revenue.activeByPlan.agency],
                ] as const
              ).map(([name, price, subs]) => (
                <tr key={name}>
                  <td>{name}</td>
                  <td>${price}/mo</td>
                  <td>{subs}</td>
                  <td>${(subs * price).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted small ops-rev-split">
            {revenue.paying.toLocaleString()} paying · {revenue.trialing} trialing ·{" "}
            {revenue.freeAccounts.toLocaleString()} free · {revenue.totalAccounts.toLocaleString()}{" "}
            accounts · {revenue.creditsOutstanding.toLocaleString()} credits outstanding
          </p>
        </section>
      )}

      <section className="card ops-section">
        <h2>System health</h2>
        {health == null ? (
          <p className="muted small">Unavailable.</p>
        ) : (
          <div className="ops-health">
            <div className={`ops-health-chip ops-tone-${health.reconnectNeeded > 0 ? "err" : "ok"}`}>
              <span className="ops-health-n">{health.reconnectNeeded}</span>
              <span className="ops-health-l">need reconnect</span>
            </div>
            <div className={`ops-health-chip ops-tone-${health.accountsWithTrouble > 0 ? "warn" : "ok"}`}>
              <span className="ops-health-n">{health.accountsWithTrouble}</span>
              <span className="ops-health-l">token warnings</span>
            </div>
            <div className="ops-health-chip ops-tone-neutral">
              <span className="ops-health-n">{health.pendingScheduled}</span>
              <span className="ops-health-l">pending scheduled</span>
            </div>
            <div className={`ops-health-chip ops-tone-${health.overdueScheduled > 0 ? "err" : "ok"}`}>
              <span className="ops-health-n">{health.overdueScheduled}</span>
              <span className="ops-health-l">
                overdue{health.oldestOverdue ? ` · ${timeAgo(health.oldestOverdue)} late` : ""}
              </span>
            </div>
            <div className={`ops-health-status ops-tone-${healthTone}`}>
              {healthTone === "ok" ? "All systems nominal" : "Needs attention"}
            </div>
          </div>
        )}
      </section>

      <div className="ops-cols">
        <section className="card ops-section">
          <h2>Live activity</h2>
          {data.activity == null ? (
            <p className="muted small">Unavailable.</p>
          ) : data.activity.length === 0 ? (
            <p className="muted small">No posts yet.</p>
          ) : (
            <ul className="ops-activity">
              {data.activity.map((a) => (
                <li key={a.id} className="ops-activity-row">
                  <span
                    className={`ops-activity-dot ops-tone-${a.failCount > 0 ? (a.okCount > 0 ? "warn" : "err") : "ok"}`}
                    aria-hidden="true"
                  />
                  <div className="ops-activity-body">
                    <div className="ops-activity-title">{a.title || "Untitled post"}</div>
                    <div className="muted small ops-activity-meta">
                      {a.email || "unknown"} · {a.platforms.length} platform
                      {a.platforms.length === 1 ? "" : "s"}
                      {a.failCount > 0 ? ` · ${a.failCount} failed` : ""}
                    </div>
                  </div>
                  <span className="muted small ops-activity-time">{timeAgo(a.at)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card ops-section">
          <h2>Top users</h2>
          {data.topUsers == null ? (
            <p className="muted small">Unavailable.</p>
          ) : data.topUsers.length === 0 ? (
            <p className="muted small">No users yet.</p>
          ) : (
            <ul className="ops-users">
              {data.topUsers.map((u) => (
                <li
                  key={u.userId}
                  className="ops-user-row ops-clickable"
                  role="button"
                  tabIndex={0}
                  onClick={() => setUserDetail(u)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setUserDetail(u);
                    }
                  }}
                >
                  <div className="ops-user-main">
                    <span className="ops-user-email">{u.email || "unknown"}</span>
                    <span className={`ops-plan ops-plan-${u.plan}`}>{u.plan}</span>
                  </div>
                  <div className="ops-user-stats muted small">
                    <span>{u.posts.toLocaleString()} posts</span>
                    <span>{u.platforms} platforms</span>
                    <span>~${(u.xPosts * X_COST_PER_POST).toFixed(2)} cost</span>
                    <span>{u.lastActive ? `${timeAgo(u.lastActive)} ago` : "never"}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="card ops-section">
        <div className="ops-section-head">
          <h2>Waitlist</h2>
          {data.waitlist && data.waitlist.total > 0 && (
            <button
              type="button"
              className="btn small"
              onClick={() => void download("waitlist")}
              disabled={downloading}
            >
              {downloading ? "Preparing…" : "Download CSV"}
            </button>
          )}
        </div>
        {dlError && <p className="error">{dlError}</p>}
        {data.waitlist == null ? (
          <p className="muted small">Unavailable.</p>
        ) : data.waitlist.total === 0 ? (
          <p className="muted small">
            No signups yet — they'll appear here as people join from broadside.social.
          </p>
        ) : (
          <>
            <ul className="ops-waitlist">
              {data.waitlist.recent.map((w) => (
                <li key={w.email} className="ops-waitlist-row">
                  <span className="ops-waitlist-email">{w.email}</span>
                  <span className="muted small">{new Date(w.at).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
            {data.waitlist.total > data.waitlist.recent.length && (
              <p className="muted small">
                Showing the latest {data.waitlist.recent.length} of{" "}
                {data.waitlist.total.toLocaleString()} — download the CSV for the full list.
              </p>
            )}
          </>
        )}
      </section>

      <div className="ops-cols">
        <section className="card ops-section">
          <h2>Platform breakdown</h2>
          {accounts == null ? (
            <p className="muted small">Unavailable.</p>
          ) : breakdown.length === 0 ? (
            <p className="muted small">No connected accounts yet.</p>
          ) : (
            <ul className="ops-breakdown">
              {breakdown.map(([id, count]) => {
                const info = platformInfo(platforms, id);
                return (
                  <li key={id} className="ops-breakdown-row">
                    {info ? (
                      <PlatformIcon platform={info} size={22} />
                    ) : (
                      <span className="ops-breakdown-dot" aria-hidden="true" />
                    )}
                    <span className="ops-breakdown-name">{info?.name ?? id}</span>
                    <span className="ops-breakdown-count">{count.toLocaleString()}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="card ops-section">
          <h2>Recent failures</h2>
          {data.recentFailures == null ? (
            <p className="muted small">Unavailable.</p>
          ) : data.recentFailures.length === 0 ? (
            <p className="muted small">No failed publishes.</p>
          ) : (
            <ul className="ops-failures">
              {data.recentFailures.map((f) => (
                <li
                  key={f.id}
                  className="ops-failure ops-clickable"
                  role="button"
                  tabIndex={0}
                  onClick={() => setDetail(f)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setDetail(f);
                    }
                  }}
                >
                  <div className="ops-failure-head">
                    <span className="ops-failure-title">{f.title || "Untitled post"}</span>
                    <span className="muted small">{new Date(f.at).toLocaleString()}</span>
                  </div>
                  <div className="ops-failure-legs">
                    {f.failures.map((leg, i) => {
                      const info = platformInfo(platforms, leg.platform);
                      return (
                        <div className="ops-failure-leg" key={`${leg.platform}-${i}`}>
                          <span className="tag ops-failure-platform">{info?.name ?? leg.platform}</span>
                          <span className="muted small">{leg.reason}</span>
                        </div>
                      );
                    })}
                  </div>
                  <span className="ops-failure-cta muted small">Click to diagnose →</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {detail && (
        <div className="ops-modal-backdrop" onClick={() => setDetail(null)}>
          <div
            className="ops-modal card"
            role="dialog"
            aria-modal="true"
            aria-label="Failure details"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ops-modal-head">
              <div>
                <strong>{detail.title || "Untitled post"}</strong>
                <div className="muted small">{new Date(detail.at).toLocaleString()}</div>
              </div>
              <button type="button" className="btn small ghost" onClick={() => setDetail(null)}>
                Close
              </button>
            </div>
            <ul className="ops-modal-legs">
              {detail.failures.map((leg, i) => {
                const info = platformInfo(platforms, leg.platform);
                const d = diagnose(leg.reason);
                return (
                  <li key={`${leg.platform}-${i}`} className="ops-modal-leg">
                    <span className="tag ops-failure-platform">{info?.name ?? leg.platform}</span>
                    <p className="ops-modal-reason muted small">{leg.reason}</p>
                    <p className="ops-modal-diag">
                      <span className="ops-modal-k">What&apos;s going on</span>
                      {d.diagnosis}
                    </p>
                    <p className="ops-modal-action">
                      <span className="ops-modal-k">Suggested fix</span>
                      {d.action}
                    </p>
                  </li>
                );
              })}
            </ul>
            <div className="ops-modal-foot">
              <button type="button" className="btn small" onClick={() => void copyForClaude(detail)}>
                {copied ? "Copied ✓" : "Copy for Claude"}
              </button>
              <span className="muted small">Paste it into chat and I&apos;ll take it from there.</span>
            </div>
          </div>
        </div>
      )}

      {userDetail &&
        (() => {
          const u = userDetail;
          const prices = data.revenue?.prices;
          const isPaying = u.status === "active" || u.status === "trialing";
          const mrr =
            isPaying && prices
              ? u.plan === "starter"
                ? prices.starter
                : u.plan === "pro"
                  ? prices.pro
                  : u.plan === "agency"
                    ? prices.agency
                    : 0
              : 0;
          const xCost = u.xPosts * X_COST_PER_POST;
          const byPlatform = Object.entries(u.byPlatform).sort((a, b) => b[1] - a[1]);
          return (
            <div className="ops-modal-backdrop" onClick={() => setUserDetail(null)}>
              <div
                className="ops-modal card"
                role="dialog"
                aria-modal="true"
                aria-label="User details"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="ops-modal-head">
                  <div>
                    <strong>{u.email || "unknown"}</strong>
                    <div className="muted small">
                      <span className={`ops-plan ops-plan-${u.plan}`}>{u.plan}</span>{" "}
                      {u.status ? `· ${u.status}` : "· no subscription"}
                    </div>
                  </div>
                  <button type="button" className="btn small ghost" onClick={() => setUserDetail(null)}>
                    Close
                  </button>
                </div>

                <div className="ops-udetail-grid">
                  <div>
                    <span className="ops-modal-k">Joined</span>
                    {u.joined ? new Date(u.joined).toLocaleDateString() : "—"}
                  </div>
                  <div>
                    <span className="ops-modal-k">Last active</span>
                    {u.lastActive ? `${timeAgo(u.lastActive)} ago` : "never"}
                  </div>
                  <div>
                    <span className="ops-modal-k">Credits</span>
                    {u.credits.toLocaleString()}
                  </div>
                  <div>
                    <span className="ops-modal-k">Total posts</span>
                    {u.posts.toLocaleString()}
                  </div>
                </div>

                <div className="ops-udetail-econ">
                  <div className="ops-rev-big">
                    <span className="ops-rev-n">${mrr}</span>
                    <span className="ops-rev-l">MRR</span>
                  </div>
                  <div className="ops-rev-big">
                    <span className="ops-rev-n ops-cost">~${xCost.toFixed(2)}</span>
                    <span className="ops-rev-l">est. X cost · lifetime</span>
                  </div>
                </div>

                <span className="ops-modal-k">Posts by platform</span>
                {byPlatform.length === 0 ? (
                  <p className="muted small">No posts yet.</p>
                ) : (
                  <ul className="ops-breakdown">
                    {byPlatform.map(([id, count]) => {
                      const info = platformInfo(platforms, id);
                      return (
                        <li key={id} className="ops-breakdown-row">
                          {info ? (
                            <PlatformIcon platform={info} size={20} />
                          ) : (
                            <span className="ops-breakdown-dot" aria-hidden="true" />
                          )}
                          <span className="ops-breakdown-name">{info?.name ?? id}</span>
                          <span className="ops-breakdown-count">{count.toLocaleString()}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          );
        })()}
    </div>
  );
}
